// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { Link } from 'react-router-dom'
import { GuardRokIcon } from '../components/GuardRokLogo'

const LAST_UPDATED = 'May 27, 2026'
const CONTACT = 'privacy@guardrok.com'

export default function DoNotSell() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#030712',
        color: '#fff',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {/* Nav */}
      <nav
        style={{
          padding: '20px 48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #0f172a',
          position: 'sticky',
          top: 0,
          background: 'rgba(3,9,18,0.95)',
          backdropFilter: 'blur(12px)',
          zIndex: 100,
        }}
      >
        <Link
          to="/"
          style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}
        >
          <GuardRokIcon size={36} />
          <div>
            <div
              style={{
                fontWeight: 800,
                fontSize: 18,
                letterSpacing: '-0.5px',
                lineHeight: 1,
                color: '#fff',
              }}
            >
              Guard<span style={{ color: '#E63946' }}>Rok</span>
              <span
                style={{ fontSize: 8, color: '#6b7280', verticalAlign: 'super', marginLeft: 1 }}
              >
                ™
              </span>
            </div>
            <div
              style={{
                fontSize: 9,
                letterSpacing: '3px',
                color: '#6b7280',
                textTransform: 'uppercase' as const,
                marginTop: 2,
              }}
            >
              AI Firewall
            </div>
          </div>
        </Link>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Link to="/privacy" style={{ color: '#9ca3af', fontSize: 14, textDecoration: 'none' }}>
            Privacy
          </Link>
          <Link to="/login" style={{ color: '#9ca3af', fontSize: 14, textDecoration: 'none' }}>
            Sign in
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '64px 40px 96px' }}>
        <div style={{ fontSize: 12, color: '#475569', marginBottom: 12 }}>
          Last updated: {LAST_UPDATED}
        </div>
        <h1 style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-1px', marginBottom: 10 }}>
          Do Not Sell or Share My Personal Information
        </h1>
        <p style={{ color: '#64748b', fontSize: 11, marginBottom: 48, lineHeight: 1.7 }}>
          This page describes your rights under the California Consumer Privacy Act (CCPA) and
          California Privacy Rights Act (CPRA) regarding the sale or sharing of your personal
          information.
        </p>

        {/* Key statement */}
        <div
          style={{
            background: '#0a1628',
            border: '1px solid #1e3a5f',
            borderRadius: 12,
            padding: '24px 28px',
            marginBottom: 48,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 800, color: '#60a5fa', marginBottom: 8 }}>
            Our Position
          </div>
          <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.8, margin: 0 }}>
            <strong style={{ color: '#f1f5f9' }}>
              GuardRok does not sell or share your personal information
            </strong>{' '}
            with third parties for monetary or other valuable consideration, and we do not use it
            for cross-context behavioral advertising. We are a B2B security product — our business
            model is subscription revenue, not data monetization.
          </p>
        </div>

        {[
          {
            title: 'What this means for you',
            body: [
              'We do not sell your name, email address, company, or usage data to data brokers, advertisers, or any third party for commercial purposes.',
              'We do not share your personal information with third parties for targeted or cross-context behavioral advertising.',
              "Your prompt metadata (timestamps, risk categories, action taken) is used solely to provide GuardRok's services to your organization — never for advertising or data resale.",
              'Prompt text is never stored at rest. We physically cannot sell data we do not retain.',
            ],
          },
          {
            title: 'Your CCPA / CPRA rights (California residents)',
            body: [
              'Right to Know — You may request disclosure of the personal information we have collected about you and how it is used.',
              'Right to Delete — You may request deletion of your personal information, subject to certain exceptions.',
              'Right to Correct — You may request correction of inaccurate personal information we hold.',
              'Right to Opt Out — Although we do not sell or share personal information, you may submit a formal opt-out request to be documented in our records.',
              'Right to Non-Discrimination — We will not discriminate against you for exercising any of these rights.',
            ],
          },
          {
            title: 'How to submit a request',
            body: [
              `Email us at ${CONTACT} with the subject line "CCPA Rights Request".`,
              'Include your full name, email address associated with your GuardRok account, and the specific right you wish to exercise.',
              'We will respond within 45 days as required by California law. We may need to verify your identity before fulfilling the request.',
              "For requests from authorised agents acting on a consumer's behalf, please include written proof of authorisation.",
            ],
          },
          {
            title: 'Data we collect and why',
            body: [
              'Account data (email, organization name) — to provide and manage your subscription.',
              'Usage metadata (prompt counts, violation counts, timestamps) — to generate your compliance reports and enforce quotas.',
              "Extension telemetry (blocked/allowed events) — to power your organization's security dashboard.",
              'We do not collect prompt text, and we do not run advertising scripts or sell data to any party.',
            ],
          },
          {
            title: 'Third-party service providers',
            body: [
              'We work with a limited number of service providers (infrastructure, payments, and security) who process only the minimum data necessary to deliver the service.',
              'No service provider is authorised to use your data for their own marketing or to sell it to any third party.',
              'See our Privacy Policy for details.',
            ],
          },
        ].map(({ title, body }) => (
          <div key={title} style={{ marginBottom: 36 }}>
            <h2 style={{ fontSize: 12, fontWeight: 700, color: '#f1f5f9', marginBottom: 12 }}>
              {title}
            </h2>
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column' as const,
                gap: 8,
              }}
            >
              {body.map((line, i) => (
                <li
                  key={i}
                  style={{
                    fontSize: 11,
                    color: '#94a3b8',
                    lineHeight: 1.75,
                    paddingLeft: 16,
                    position: 'relative' as const,
                  }}
                >
                  <span style={{ position: 'absolute' as const, left: 0, color: '#374151' }}>
                    ·
                  </span>
                  {line}
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div
          style={{
            background: '#0d1117',
            border: '1px solid #1f2937',
            borderRadius: 10,
            padding: '20px 24px',
            marginTop: 16,
          }}
        >
          <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.8 }}>
            <strong style={{ color: '#f1f5f9' }}>Contact:</strong>{' '}
            <a href={`mailto:${CONTACT}`} style={{ color: '#E63946', textDecoration: 'none' }}>
              {CONTACT}
            </a>
            {' · '}aiApas LLC, United States
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          borderTop: '1px solid #0f172a',
          padding: '20px 48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ fontSize: 12, color: '#374151' }}>© 2026 GuardRok™ · aiApas LLC</div>
        <div style={{ display: 'flex', gap: 20 }}>
          <Link to="/privacy" style={{ color: '#374151', fontSize: 12, textDecoration: 'none' }}>
            Privacy
          </Link>
          <Link to="/terms" style={{ color: '#374151', fontSize: 12, textDecoration: 'none' }}>
            Terms
          </Link>
          <Link to="/cookies" style={{ color: '#374151', fontSize: 12, textDecoration: 'none' }}>
            Cookies
          </Link>
          <Link
            to="/do-not-sell"
            style={{ color: '#374151', fontSize: 12, textDecoration: 'none' }}
          >
            Do Not Sell
          </Link>
        </div>
      </div>
    </div>
  )
}
